import React, { useEffect, useState, useMemo } from 'react';
import SectionWrapper from './SectionWrapper';
import FormInput from './FormInput';
import CollapsibleSection from './CollapsibleSection';
import { Button } from '@mui/material';
import useApi from '../../../../Hooks/useApi';
import { fetchSectionWiseData, postSectionWiseData, updateSectionData } from '../../../../Utils/cityProfileManagementUtils';
import showToast from '../../../../Helpers/toast';

const fetchPublishedStatus = async (locationId, year) => {
    return new Promise(resolve => {
        setTimeout(() => {
            resolve({ published: false });
        }, 300);
    });
};

const normalizeKey = (indicator) =>
    indicator.toLowerCase().replace(/\s+/g, '').replace(/[^\w]/g, '');

const DynamicSectionForm = ({ section, title, sections, city, year, disabledText, column, onSubmitSuccess }) => {
    const [data, setData] = useState({});
    const [fieldIds, setFieldIds] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState(null);
    const [published, setPublished] = useState(false);

    const { request: publicRequest } = useApi({ isPublic: true });
    const { request: requestPrivate } = useApi();

    const hasChanges = useMemo(() => {
        if (published) return false;
        const isEdit = Object.keys(fieldIds).length > 0;
        if (isEdit) {
            return Object.entries(data).some(([key, value]) => !!value?.toString().trim());
        } else {
            return sections?.some(section =>
                section.fields.some(field => data[field.name]?.toString().trim())
            );
        }
    }, [data, fieldIds, published, sections]);

    useEffect(() => {
        if (!city || !year) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await fetchSectionWiseData(publicRequest, { section, location: city, year });
                console.log(res);

                const flatData = {};
                const ids = {};
                Object.values(res).forEach(sec => {
                    sec.forEach(({ indicator, value, id }) => {
                        const key = normalizeKey(indicator);
                        const fieldConfig = sections.flatMap(s => s.fields).find(f => normalizeKey(f.label) === key);
                        const unit = fieldConfig?.unit || '';

                        // Strip unit from value string if present
                        let numericValue = value;
                        if (unit && typeof value === 'string') {
                            numericValue = value.replace(unit, '').trim();
                        }

                        flatData[key] = numericValue;
                        if (id) ids[key] = id;
                    });
                });


                setData(flatData);
                setFieldIds(ids);

                const pubRes = await fetchPublishedStatus(city, year);
                setPublished(pubRes.published);
            } catch (err) {
                setError('Failed to load data');
                setData({});
                setFieldIds({});
                setPublished(false);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [city, year, section, publicRequest, sections]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (!published && /^\d*(\.\d*)?$/.test(value)) {
            setData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (published) return;
        setSubmitLoading(true);

        try {
            const isEdit = Object.keys(fieldIds).length > 0;

            if (isEdit) {
                const putPayload = Object.entries(data).map(([key, value]) => {
                    const field = sections.flatMap(sec => sec.fields).find(f => f.name === key);
                    const unit = field?.unit;
                    return {
                        id: fieldIds[key],
                        value: unit ? `${value} ${unit}` : value,
                        icon: null
                    };
                });
                const res = await updateSectionData(requestPrivate, { putPayload });
                showToast({ message: res.message, type: res.success ? 'success' : 'error' });
                onSubmitSuccess();
            } else {
                const postPayload = {
                    year: Number(year),
                    locationId: Number(city),
                    createCityProfileSectionRequestList: []
                };

                sections.forEach(sectionObj => {
                    sectionObj.fields.forEach((field, index) => {
                        const value = data[field.name];
                        if (value) {
                            postPayload.createCityProfileSectionRequestList.push({
                                section,
                                category: sectionObj.title,
                                categoryOrder: sectionObj.order,
                                indicator: field.label,
                                indicatorOrder: index + 1,
                                value: field.unit ? `${value} ${field.unit}` : value,
                                icon: field.icon || null
                            });
                        }
                    });
                });

                const res = await postSectionWiseData(requestPrivate, { postPayload });
                showToast({ message: res.message, type: res.success ? 'success' : 'error' });
                onSubmitSuccess();
            }
        } catch (err) {
            showToast({
                message: err?.response?.data?.message || 'Unexpected error occurred',
                type: 'error'
            });
        } finally {
            setSubmitLoading(false);
        }
    };

    if (loading) return <p>Loading {title.toLowerCase()} data...</p>;
    if (error) return <p className="text-red-600">Error: {error}</p>;

    return (
        <SectionWrapper title={title}>
            {published && (
                <p className="text-sm text-red-600 mt-1 mb-3">
                    {disabledText || 'Note: This section is published and cannot be edited.'}
                </p>
            )}
            {
                Array.isArray(sections) && sections.length > 0 ? 
                <form onSubmit={handleSubmit} className="space-y-6">
                {sections.map(sectionObj => (
                    <CollapsibleSection key={sectionObj.title} title={sectionObj.title}>
                        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-${column} gap-4`}>
                            {sectionObj.fields.map(field => (
                                <FormInput
                                    key={field.name}
                                    name={field.name}
                                    label={field.label}
                                    unit={field.unit}
                                    value={data[field.name] || ''}
                                    onChange={handleChange}
                                    disabled={published}
                                />
                            ))}
                        </div>
                    </CollapsibleSection>
                ))}

                {!published && (
                    <div className="pt-4 text-center">
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={!hasChanges || submitLoading}
                        >
                            {submitLoading ? `Saving...` : `Submit ${title}`}
                        </Button>
                    </div>
                )}
            </form>
            : <p>No sections available.</p>
            }
        </SectionWrapper>
    );
};

export default DynamicSectionForm;